import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TenantAssets, DEFAULT_TENANT_ASSETS } from './tenant-theme.interface';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class TenantAssetService {
  private assetsSubject = new BehaviorSubject<TenantAssets>(DEFAULT_TENANT_ASSETS);
  assets$: Observable<TenantAssets> = this.assetsSubject.asObservable();

  /**
   * Los MISMOS assets como signal. Existe porque los consumidores del logo
   * (sidebar, navbar) son OnPush: leyendo un signal se marcan sucios solos al
   * cambiar de marca. Con el getter de siempre, el logo solo se actualizaba
   * cuando algo más disparaba la detección de cambios — de ahí que el sidebar
   * conservara el logo anterior tras cambiar de tenant.
   */
  readonly assets = signal<TenantAssets>(DEFAULT_TENANT_ASSETS);

  apply(assets: Partial<TenantAssets>): void {
    const resolved: TenantAssets = {
      logoUrl: this.resolveUrl(assets.logoUrl, DEFAULT_TENANT_ASSETS.logoUrl!),
      logoSmallUrl: this.resolveUrl(assets.logoSmallUrl, DEFAULT_TENANT_ASSETS.logoSmallUrl!),
      faviconUrl: this.resolveUrl(assets.faviconUrl, null),
    };
    this.assetsSubject.next(resolved);
    this.assets.set(resolved);
  }

  getLogoUrl(): string {
    return this.assetsSubject.value.logoUrl ?? DEFAULT_TENANT_ASSETS.logoUrl!;
  }

  getLogoSmallUrl(): string {
    return this.assetsSubject.value.logoSmallUrl ?? DEFAULT_TENANT_ASSETS.logoSmallUrl!;
  }

  getFaviconUrl(): string | null {
    return this.assetsSubject.value.faviconUrl;
  }

  getSnapshot(): Readonly<TenantAssets> {
    return this.assetsSubject.value;
  }

  reset(): void {
    this.assetsSubject.next(DEFAULT_TENANT_ASSETS);
    this.assets.set(DEFAULT_TENANT_ASSETS);
  }

  /**
   * Resuelve la URL de un asset del tenant.
   * - URL absoluta (http/https) → se usa tal cual (la API ya incluye el dominio del CDN)
   * - Path relativo + tenantCdnUrl configurado → se concatena con el CDN base
   * - null/vacío → se usa el fallback local
   */
  private resolveUrl(url: string | null | undefined, fallback: string | null): string | null {
    if (!url) {
      return fallback;
    }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Un archivo SUBIDO por la pantalla de Marcas lo sirve NUESTRA API, y el
    // theme lo publica como ruta (`/api/v1/tenants/<id>/assets/<slot>/file/`).
    // Sin esta rama caía al `fallback` —el logo de Polipay—, así que una marca
    // configurada 100% por endpoint nunca veía su propio logo: solo funcionaban
    // las que declaraban una URL absoluta.
    if (url.startsWith('/api/')) {
      return `${environment.apiUrl.replace(/\/+$/, '')}${url}`;
    }
    const cdnBase = environment.tenantCdnUrl;
    if (cdnBase) {
      const base = cdnBase.replace(/\/+$/, '');
      const path = url.replace(/^\/+/, '');
      return `${base}/${path}`;
    }
    return fallback;
  }
}
